import { useOverlay } from "@base/model/hooks/useOverlay";
import { Button } from "@base/ui/Button/Button";
import { membershipQueries } from "@features/membership/api/queryFactory";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import MembershipAndPaymentInfoBox from "./MembershipAndPaymentInfoBox";
import PaymentItem from "./PaymentItem";
import SettingsDialog from "./SettingsDialog";

const Membership = () => {
  const { t } = useTranslation();
  const { open } = useOverlay();
  const navigate = useNavigate();

  const [currentPage, setCurrentPage] = useState(1);

  const { data: membership, isError: isMembershipError } = useQuery(membershipQueries.membership());
  const { data: paymentHistory } = useQuery({
    ...membershipQueries.paymentHistory(currentPage),
    enabled: !!membership,
  });

  const results = paymentHistory?.results ?? [];
  const total_pages = paymentHistory?.total_pages ?? 1;

  const handleOpenSettingsDialog = () => {
    open(({ isOpen, close }) => <SettingsDialog isOpen={isOpen} onClose={close} />);
  };

  const handleApplyClick = () => {
    navigate({ to: "/apply" });
  };

  const isPaymentHistoryVisible = !isMembershipError && !!membership;

  return (
    <div className="space-y-8 md:space-y-10">
      <section className="flex flex-col gap-4">
        <MembershipAndPaymentInfoBox onSettingsClick={handleOpenSettingsDialog} onApplyClick={handleApplyClick} />
      </section>
      {isPaymentHistoryVisible && (
        <section className="space-y-4">
          <h2 className="text-title4 tracking-[-0.48px] text-gray-1000">{t("paymentHistory")}</h2>
          <div className="flex flex-col gap-2 mt-8">
            {results.map((payment, index) => (
              <PaymentItem key={`${payment.payment_item}-${payment.paid_date}-${index}`} {...payment} />
            ))}
          </div>
          {total_pages > 1 && (
            <div className="flex gap-2 justify-center items-center mt-4">
              <Button
                type="button"
                variant="outline"
                size="small"
                disabled={currentPage <= 1}
                onClick={() => setCurrentPage((p) => p - 1)}
              >
                {t("prev")}
              </Button>
              <span className="min-w-[4rem] text-center text-body3 text-gray-900">
                {t("pageOf", { current: currentPage, total: total_pages })}
              </span>
              <Button
                type="button"
                variant="outline"
                size="small"
                disabled={currentPage >= total_pages}
                onClick={() => setCurrentPage((p) => p + 1)}
              >
                {t("next")}
              </Button>
            </div>
          )}
        </section>
      )}
    </div>
  );
};

export default Membership;
